import { reactive } from 'vue'
import { format } from 'date-fns'
import { useAuthStore } from '@/store/auth'
import NotificationService from '@/services/NotificationService'
import NotifikacieService from '@/services/NotifikacieService'
import SystemFeedbackService from '@/services/SystemFeedbackService'

/*
 * Čo používateľovi „svieti“: počty v menu (úlohy, novinky, požiadavky, podnety) a upozornenia v aplikácii
 * (zvonček, karta „Upozornenia“ na Prehľade). Stav je zdieľaný (singleton): kým je zobrazená navigácia,
 * načítava sa spolu raz za 30 s, po akcii (prečítanie upozornenia, otvorenie detailu záznamu) hneď.
 *
 * Detail záznamu pri otvorení:  precitatObjekt('uloha' | 'poziadavka' | 'prikaz', id)
 * Okamžité obnovenie po akcii:  obnovitUpozornenia()
 */
const INTERVAL_MS = 30000
const LIMIT = 20

/** Počty v menu - podľa backendu to, čo čaká na používateľa alebo má neprečítané upozornenie */
export const counts = reactive({ tasks: 0, news: 0, requests: 0, feedback: 0, systemFeedback: 0 })

/** Posledné upozornenia (neprečítané najprv) a počet všetkých neprečítaných */
export const upozornenia = reactive({ polozky: [], neprecitane: 0, nacitane: false })

/* ---- Počty v menu */

export async function obnovitPocty() {
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) return
  try {
    const notifications = await NotificationService.getAllNotifications()
    counts.tasks = notifications.tasks || 0
    counts.news = notifications.news || 0
    counts.requests = notifications.requests || 0
    counts.feedback = notifications.feedback || 0
  } catch (error) {
    console.error('Chyba pri načítaní notifikácií:', error)
  }
  // Hlásenia pre vývojárov — endpoint je len pre adminov
  if (authStore.user?.rola === 'admin') {
    try {
      const response = await SystemFeedbackService.getNewCount()
      counts.systemFeedback = response.data.count || 0
    } catch (error) {
      console.error('Chyba pri načítaní počtu hlásení:', error)
    }
  }
}

/* ---- Upozornenia */

// Každá zmena (prečítanie, odhlásenie) zvýši verziu - odpoveď skoršieho načítania sa potom zahodí,
// aby neprepísala práve prečítané upozornenie späť na neprečítané
let verzia = 0
let nacitavanie = null

const zmena = () => {
  nacitavanie = null
  verzia += 1
  return verzia
}

const nastavNeprecitane = (moja, pocet) => {
  if (moja === verzia && typeof pocet === 'number') upozornenia.neprecitane = pocet
}

/** Načíta upozornenia. Ak načítanie práve beží, vráti ho (navigácia, zvonček aj Prehľad naraz = jedna požiadavka). */
export function nacitajUpozornenia() {
  if (!useAuthStore().isAuthenticated) return Promise.resolve()
  if (nacitavanie) return nacitavanie

  const moja = verzia
  const poziadavka = NotifikacieService.zoznam({ limit: LIMIT })
    .then(({ data }) => {
      if (moja !== verzia) return
      upozornenia.polozky = Array.isArray(data.data) ? data.data : []
      upozornenia.neprecitane = data.neprecitane || 0
    })
    .catch((error) => console.error('Chyba pri načítaní upozornení:', error))
    .finally(() => {
      if (moja === verzia) upozornenia.nacitane = true
      if (nacitavanie === poziadavka) nacitavanie = null
    })
  nacitavanie = poziadavka
  return poziadavka
}

/** Okamžité obnovenie zvončeka aj počtov v menu - po akcii, ktorá mohla vytvoriť alebo prečítať upozornenie */
export function obnovitUpozornenia() {
  zmena()
  return Promise.all([nacitajUpozornenia(), obnovitPocty()])
}

/** Označí upozornenie ako prečítané (klik v zvončeku alebo na Prehľade) */
export async function precitat(upozornenie) {
  if (!upozornenie || upozornenie.precitane) return
  const moja = zmena()
  upozornenie.precitane = true
  upozornenia.neprecitane = Math.max(0, upozornenia.neprecitane - 1)
  try {
    const { data } = await NotifikacieService.precitat(upozornenie.id)
    nastavNeprecitane(moja, data.neprecitane)
  } catch (error) {
    console.error('Chyba pri označení upozornenia:', error)
    nacitajUpozornenia()
  }
  // Úloha / požiadavka s neprečítaným upozornením svieti v menu - po prečítaní sa počty môžu zmeniť
  obnovitPocty()
}

/** Označí všetky upozornenia ako prečítané. Pri chybe vráti false (volajúci zobrazí hlášku). */
export async function precitatVsetko() {
  const moja = zmena()
  upozornenia.polozky.forEach((upozornenie) => {
    upozornenie.precitane = true
  })
  upozornenia.neprecitane = 0
  try {
    const { data } = await NotifikacieService.precitatVsetko()
    nastavNeprecitane(moja, data.neprecitane)
    return true
  } catch (error) {
    console.error('Chyba pri označení upozornení:', error)
    nacitajUpozornenia()
    return false
  } finally {
    obnovitPocty()
  }
}

/**
 * Prečíta upozornenia k záznamu - volá detail úlohy, požiadavky a konceptu pri otvorení.
 * Ak sa niečo zmenilo, hneď obnoví zvonček aj počty v menu.
 * @param {'uloha'|'poziadavka'|'prikaz'|'podnet'} objektTyp
 * @param {number|string} objektId
 */
export async function precitatObjekt(objektTyp, objektId) {
  if (!useAuthStore().isAuthenticated) return
  try {
    const { data } = await NotifikacieService.precitatObjekt(objektTyp, Number(objektId))
    if (data && data.updated > 0) await obnovitUpozornenia()
  } catch (error) {
    console.error('Chyba pri označení upozornení k záznamu:', error)
  }
}

/* ---- Pravidelné načítavanie (spúšťa ho navigácia, ktorá je zobrazená po celý čas prihlásenia) */

let sledujuci = 0
let casovac = null
let zmeskane = false

const tik = () => {
  // Skrytá karta prehliadača nenačítava - po návrate na kartu sa obnoví hneď
  if (document.hidden) {
    zmeskane = true
    return
  }
  obnovitPocty()
  nacitajUpozornenia()
}

const poZobrazeni = () => {
  if (!document.hidden && zmeskane) {
    zmeskane = false
    tik()
  }
}

/** Prvý odberateľ načíta hneď a spustí načítavanie každých 30 s */
export function zacniSledovanie() {
  sledujuci += 1
  if (sledujuci > 1) return
  zmeskane = false
  tik()
  casovac = setInterval(tik, INTERVAL_MS)
  document.addEventListener('visibilitychange', poZobrazeni)
}

/** Posledný odberateľ (odhlásenie) zastaví načítavanie a vymaže stav - upozornenia ani počty nesmú ostať ďalšiemu používateľovi */
export function ukonciSledovanie() {
  sledujuci = Math.max(0, sledujuci - 1)
  if (sledujuci > 0) return
  clearInterval(casovac)
  casovac = null
  document.removeEventListener('visibilitychange', poZobrazeni)
  zmena()
  upozornenia.polozky = []
  upozornenia.neprecitane = 0
  upozornenia.nacitane = false
  Object.keys(counts).forEach((kluc) => {
    counts[kluc] = 0
  })
}

/* ---- Zobrazenie upozornenia */

const IKONY = {
  komentar: 'mdi-comment-text-outline',
  uloha_nova: 'mdi-clipboard-text-outline',
  uloha_splnena: 'mdi-check-circle-outline',
  uloha_stav: 'mdi-progress-clock',
  uloha_ping: 'mdi-bell-ring',
  uloha_termin: 'mdi-clock-alert-outline',
  poziadavka_nova: 'mdi-inbox',
  poziadavka_stav: 'mdi-progress-clock',
  poziadavka_presun: 'mdi-transfer-right',
  podnet_odpoved: 'mdi-lightbulb-outline',
}

/** Ikona podľa typu upozornenia */
export const ikonaUpozornenia = (typ) => IKONY[typ] || 'mdi-bell-outline'

/** Odkaz upozornenia - len cesta v aplikácii (napr. /ulohy/12?komentar=55), inak null */
export const odkazUpozornenia = (upozornenie) => {
  const odkaz = upozornenie && upozornenie.odkaz
  return typeof odkaz === 'string' && odkaz.startsWith('/') && !odkaz.startsWith('//') ? odkaz : null
}

/** Zlúčené opakované udalosti: „3 komentáre“, „5 komentárov“, pri iných typoch „3×“ */
export const opakovanieUpozornenia = (upozornenie) => {
  const pocet = upozornenie ? Number(upozornenie.pocet) : 0
  if (!(pocet > 1)) return ''
  if (upozornenie.typ === 'komentar') return `${pocet} ${pocet < 5 ? 'komentáre' : 'komentárov'}`
  return `${pocet}×`
}

/** „1 neprečítané“, „3 neprečítané“, „5 neprečítaných“ */
export const pocetNeprecitanychText = (pocet) => `${pocet} ${pocet >= 5 ? 'neprečítaných' : 'neprečítané'}`

const zaciatokDna = (datum) => new Date(datum.getFullYear(), datum.getMonth(), datum.getDate())

/** Relatívny čas: „práve teraz“, „pred 5 min“, „pred 3 h“, „včera“, „pred 4 dňami“, staršie dátumom */
export function relativnyCas(hodnota, teraz = new Date()) {
  const datum = new Date(hodnota)
  if (!hodnota || Number.isNaN(datum.getTime())) return ''
  const minuty = Math.floor((teraz - datum) / 60000)
  if (minuty < 1) return 'práve teraz'
  if (minuty < 60) return `pred ${minuty} min`
  const hodiny = Math.floor(minuty / 60)
  if (hodiny < 24) return `pred ${hodiny} h`
  const dni = Math.round((zaciatokDna(teraz) - zaciatokDna(datum)) / 86400000)
  if (dni <= 1) return 'včera'
  if (dni < 7) return `pred ${dni} dňami`
  return format(datum, datum.getFullYear() === teraz.getFullYear() ? 'd. M.' : 'd. M. yyyy')
}

/** Presný čas do popisu (title) pri relatívnom čase */
export function presnyCas(hodnota) {
  const datum = new Date(hodnota)
  return !hodnota || Number.isNaN(datum.getTime()) ? '' : format(datum, 'd. M. yyyy HH:mm')
}

export function useNotifikacie() {
  return {
    upozornenia,
    counts,
    nacitajUpozornenia,
    obnovitUpozornenia,
    precitat,
    precitatVsetko,
    precitatObjekt,
    ikonaUpozornenia,
    odkazUpozornenia,
    opakovanieUpozornenia,
    pocetNeprecitanychText,
    relativnyCas,
    presnyCas,
  }
}
